import find from 'lodash/find'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, memo, ReactNode, useMemo } from 'react'

import { useAmplitude } from '@Analytics/hook'
import { RTEContent } from '@Components/RTEContent'
import { AccordionSidePane } from '@Domains/Product/Features/Display/AccordionSidePane/AccordionSidePane'
import { BaseAttribute, Product } from '@Generated/graphql'
import { convertRTEToHTML } from '@Globals/CMS/FieldHelpers'
import { useTranslation } from '@Globals/Translations'

import styles from './ProductDescription.module.scss'

interface ProductDescriptionProps {
  product?: Product
}

export const ProductDescription: FC<ProductDescriptionProps> = memo(({ product }) => {
  const { t } = useTranslation()
  const { locale = '' } = useRouter()
  const ampli = useAmplitude()

  const description = useMemo<ReactNode>(() => {
    if (!product) {
      return null
    }

    const descriptionGenerated = (
      (find(product?.masterVariant?.attributes, ['key', 'descriptionGenerated']) as BaseAttribute)?.value || undefined
    ) as Record<string, string> | undefined

    if (descriptionGenerated?.[locale]) {
      try {
        const parsed = JSON.parse(descriptionGenerated[locale])
        if (typeof parsed === 'object' && 'rte' in parsed && parsed.rte) {
          return <RTEContent html={convertRTEToHTML({ key: 'product.description', value: parsed.rte })} />
        }
      } catch {
        // ignore errors
      }
    }

    if (!product?.description) {
      return null
    }

    return map(
      product.description.trim().split('\n'),
      (line, index) => {
        const trimmedData = line.trim()
        return trimmedData.length
          ? <p key={`${index}${line}`}>{trimmedData}</p>
          : null
      },
    )
  }, [locale, product])

  if (!product || !description) {
    return null
  }

  return (
    <AccordionSidePane
      onOpen={() => {
        ampli.productDescriptionOpen({
          source_id: 'inherit',
        })
      }}
      prerender
      title={t('products:DESCRIPTION')}
    >
      {description}

      {product.type === 'piercings' && (
        <p>
          <strong>
            {t('products:PIERCINGS_ONLY_RETURNABLE_IN_UNOPENED_PACKAGING')}
          </strong>
        </p>
      )}

      {
          (product.type === 'socks' || product.type === 'boxers')
            && (
              <p>
                <strong>
                  {t('products:PIERCINGS_ONLY_RETURNABLE_IN_UNOPENED_PACKAGING')}
                </strong>
              </p>
            )
        }

      <div className={styles.productId}>
        {`ID: ${product.id}`}
      </div>
    </AccordionSidePane>
  )
})
